import { html, css, nothing } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';
import { buttonStyles } from '../styles/button-styles.js';
import '../ui/modal-dialog.js';
import type { ModalDialog } from '../ui/modal-dialog.js';

interface ConflictSliceInfo {
    id: string;
    name: string;
    artist?: string | null;
    difficulty?: string | null;
}

/**
 * Conflict resolution dialog for duplicate slice names.
 * Shows existing slice info with 3 options: Update Existing, Create Anyway, Cancel.
 *
 * @fires update-existing - User chose to update the existing slice
 * @fires create-anyway - User chose to create despite duplicate
 * @fires conflict-cancel - User cancelled
 */
@customElement('conflict-dialog')
export class ConflictDialog extends BaseElement {
    static styles = [
        BaseElement.styles,
        buttonStyles,
        css`
            .message {
                color: var(--text-primary);
                margin: 0 0 1rem;
            }

            .conflict-slice-info {
                background: var(--background);
                padding: 1rem;
                border-radius: 6px;
                margin: 1rem 0;
                font-size: 0.9rem;
                color: var(--text-primary);
            }

            .conflict-slice-info p {
                margin: 0.25rem 0;
            }

            .conflict-slice-info strong {
                color: var(--text-secondary);
            }

            .actions {
                display: flex;
                gap: 1rem;
                margin-top: 1.5rem;
                flex-wrap: wrap;
            }

            .actions .btn {
                flex: 1 1 auto;
            }
        `,
    ];

    @property({ type: String })
    message = 'A slice with this name already exists.';

    @property({ attribute: false })
    existingSlice: ConflictSliceInfo | null = null;

    @property({ type: Boolean, reflect: true })
    open = false;

    @property({ type: Boolean })
    loading = false;

    @query('modal-dialog')
    private _modal!: ModalDialog;

    show(): void {
        this.open = true;
        this.loading = false;
        this._modal?.show();
    }

    hide(): void {
        this.open = false;
        this.loading = false;
        this._modal?.hide();
    }

    private _onUpdateExisting(): void {
        this.dispatchEvent(
            new CustomEvent('update-existing', {
                bubbles: true,
                composed: true,
            })
        );
    }

    private _onCreateAnyway(): void {
        this.dispatchEvent(
            new CustomEvent('create-anyway', {
                bubbles: true,
                composed: true,
            })
        );
    }

    private _onCancel(): void {
        this.hide();
        this.dispatchEvent(
            new CustomEvent('conflict-cancel', {
                bubbles: true,
                composed: true,
            })
        );
    }

    private _onModalClose(): void {
        this.open = false;
        this.dispatchEvent(
            new CustomEvent('conflict-cancel', {
                bubbles: true,
                composed: true,
            })
        );
    }

    render() {
        return html`
            <modal-dialog
                heading="Slice Already Exists"
                .open=${this.open}
                size="small"
                @modal-close=${this._onModalClose}
            >
                <p class="message">${this.message}</p>
                ${this.existingSlice
                    ? html`
                          <div class="conflict-slice-info">
                              <p><strong>Name:</strong> ${this.existingSlice.name}</p>
                              ${this.existingSlice.artist
                                  ? html`<p><strong>Artist:</strong> ${this.existingSlice.artist}</p>`
                                  : nothing}
                              ${this.existingSlice.difficulty
                                  ? html`<p><strong>Difficulty:</strong> ${this.existingSlice.difficulty}</p>`
                                  : nothing}
                          </div>
                      `
                    : nothing}
                <div slot="footer" class="actions">
                    <button
                        class="btn btn-primary"
                        @click=${this._onUpdateExisting}
                        ?disabled=${this.loading}
                    >
                        ${this.loading ? 'Updating...' : 'Update Existing'}
                    </button>
                    <button
                        class="btn btn-secondary"
                        @click=${this._onCreateAnyway}
                        ?disabled=${this.loading}
                    >
                        Create Anyway
                    </button>
                    <button
                        class="btn btn-outline"
                        @click=${this._onCancel}
                    >
                        Cancel
                    </button>
                </div>
            </modal-dialog>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'conflict-dialog': ConflictDialog;
    }
}
